import { ReactNode } from "react";

import { InfoCircle } from "components/icons/generated";
import ListContentWrapper from "components/ListContentWrapper";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import ButtonIcon from "ds/components/ButtonIcon";
import Tooltip from "ds/components/Tooltip";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import useTypedContext from "hooks/useTypedContext";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import PageInfoHeaderTitle from "ds/components/PageInfoHeader/Title";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";
import SearchInput from "components/SearchInput";
import useCanManageUsers from "shared/User/access/useCanManageUsers";

import { SettingsContext } from "../Context";
import SeatLimitsNotification from "../SeatLimitsNotification";
import { SEAT_LIMITS_TOOLTIP } from "../SeatLimitsNotification/constants";
import { SeatLimitsNotificationEntity } from "../SeatLimitsNotification/types";
import useSeatLimit from "../SeatLimitsNotification/useSeats";
import { UserManagementActivationStatus } from "../types";
import UserManagementActivationBadge from "../UserManagementActivationBadge";
import UserManagementWarningCallout from "../UserManagementWarningCallout";
import { FILTERS_ORDER_SETTINGS_KEY } from "./constants";

type UsersPageLayoutProps = {
  children: ReactNode;
  openInviteDrawer?: () => void;
  openInfoDrawer: () => void;
  hasActiveSearch?: boolean;
};

const UsersPageLayout = ({
  children,
  openInviteDrawer,
  openInfoDrawer,
  hasActiveSearch,
}: UsersPageLayoutProps) => {
  const { seats } = useTypedContext(SettingsContext);
  const { isLimitReached } = useSeatLimit(seats.user);
  const { activationStatus } = useTypedContext(SettingsContext);
  const canManageUsers = useCanManageUsers();

  return (
    <>
      {activationStatus === UserManagementActivationStatus.ACTIVE && (
        <SeatLimitsNotification
          entity={SeatLimitsNotificationEntity.User}
          seats={seats.user}
          type="callout"
        />
      )}
      <UserManagementWarningCallout analyticsPage={AnalyticsPageOrganization.OrganizationUsers} />
      <PageInfoHeaderWrapper>
        <Box direction="row" align="center" __deprecatedGap="1.1rem">
          <PageInfoHeaderTitle title="Users" />
          <ButtonIcon onPress={openInfoDrawer} icon={InfoCircle} variant="ghost">
            Open info drawer
          </ButtonIcon>
          <UserManagementActivationBadge />
        </Box>

        <PageInfoHeaderActions>
          {hasActiveSearch && (
            <SearchInput
              placeholder="Search..."
              filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
            />
          )}

          {canManageUsers && openInviteDrawer && (
            <Tooltip
              active={isLimitReached}
              on={(props) => (
                <Button
                  onPress={openInviteDrawer}
                  variant="primary"
                  disabled={isLimitReached}
                  {...props}
                >
                  Invite user
                </Button>
              )}
            >
              {SEAT_LIMITS_TOOLTIP}
            </Tooltip>
          )}
        </PageInfoHeaderActions>
      </PageInfoHeaderWrapper>
      <ListContentWrapper>{children}</ListContentWrapper>
    </>
  );
};

export default UsersPageLayout;
